


import React, { useState, useEffect, useRef } from "react";
import "./Leadership.css";

const dummyImage =
  "https://images.unsplash.com/photo-1544723795-3fb6469f5b39?w=400&q=80";

const Leadership = () => {
  const [teamData, setTeamData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [slidesPerView, setSlidesPerView] = useState(3);

  const sliderRef = useRef(null);
  const autoplayRef = useRef(null);

  // ✅ Dynamic vendorId (from localStorage)
  const vendorId = localStorage.getItem("vendorId");

  const getAuthToken = () =>
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("token");

  const fetchTeamData = async () => {
    try {
      setLoading(true);

      const token = getAuthToken();

      if (!vendorId) {
        throw new Error("Vendor ID not found");
      }

      console.log("Fetching team for vendor:", vendorId);

      const response = await fetch(
        `https://backend.maamaas.com/food/api/adminteam/getall/${vendorId}`,
        {
          method: "GET",
          headers: {
            Authorization: token ? `Bearer ${token}` : "",
          },
        }
      );

      if (!response.ok) {
        throw new Error(
          response.status === 403
            ? "Authentication failed. Please login again."
            : `HTTP error! status: ${response.status}`
        );
      }

      const data = await response.json();

      const formattedData = (data || []).map((item) => ({
        id: item.teamId,
        name: item.name || "No Name",
        designation: item.designation || "No Designation",
        description: item.description || "No Description",
        image: item.image || dummyImage,
        vendorId: item.vendorId,
      }));

      setTeamData(formattedData);
      setError(null);
    } catch (err) {
      console.error("Fetch Team Error:", err);

      setError(err.message || "Failed to load team members.");

      // fallback data
      setTeamData([
        {
          id: 1,
          name: "Demo User",
          designation: "Founder",
          description: "Fallback data",
          image: dummyImage,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const updateBreakpoint = () => {
    const w = window.innerWidth;
    setIsMobile(w < 640);
    setSlidesPerView(w >= 1024 ? 3 : w >= 640 ? 2 : 1);
  };

  // ✅ FIXED useEffect (vendorId dependency)
  useEffect(() => {
    if (vendorId) {
      fetchTeamData();
    }

    updateBreakpoint();

    window.addEventListener("resize", updateBreakpoint);
    return () => window.removeEventListener("resize", updateBreakpoint);
  }, [vendorId]);

  const totalSlides = Math.max(0, teamData.length - slidesPerView + 1);

  const goToSlide = (index) => {
    const clamped = Math.max(0, Math.min(index, totalSlides - 1));
    setCurrentSlide(clamped);
  };

  const next = () =>
    goToSlide(currentSlide + 1 >= totalSlides ? 0 : currentSlide + 1);

  const prev = () =>
    goToSlide(currentSlide - 1 < 0 ? totalSlides - 1 : currentSlide - 1);

  // ✅ autoplay
  useEffect(() => {
    if (isMobile || teamData.length === 0) return;

    autoplayRef.current = setInterval(next, 2500);
    return () => clearInterval(autoplayRef.current);
  }, [isMobile, currentSlide, teamData.length, slidesPerView]);

  // 🔄 Loading UI
  if (loading) {
    return (
      <section className="ldr-section">
        <div className="ldr-loading">
          <div className="ldr-spinner" />
          <p>Loading team members…</p>
        </div>
      </section>
    );
  }

  return (
    <section className="ldr-section">
      {error && <div className="ldr-error">{error}</div>}

      {teamData.length === 0 ? (
        <p className="ldr-empty">No team members found.</p>
      ) : isMobile ? (
        // 📱 MOBILE GRID
        <div className="ldr-grid">
          {teamData.map((member) => (
            <MemberCard key={member.id} member={member} />
          ))}
        </div>
      ) : (
        // 💻 DESKTOP SLIDER
        <div className="ldr-slider-wrapper">
          <button className="ldr-arrow ldr-arrow--left" onClick={prev}>
            &#8249;
          </button>

          <div className="ldr-track-outer" ref={sliderRef}>
            <div
              className="ldr-track"
              style={{
                transform: `translateX(calc(-${currentSlide} * (100% / ${slidesPerView})))`,
              }}
            >
              {teamData.map((member) => (
                <div
                  key={member.id}
                  className="ldr-slide"
                  style={{ flex: `0 0 calc(100% / ${slidesPerView})` }}
                >
                  <MemberCard member={member} />
                </div>
              ))}
            </div>
          </div>

          <button className="ldr-arrow ldr-arrow--right" onClick={next}>
            &#8250;
          </button>

          {/* Dots */}
          <div className="ldr-dots">
            {Array.from({ length: totalSlides }).map((_, i) => (
              <button
                key={i}
                className={`ldr-dot ${
                  i === currentSlide ? "ldr-dot--active" : ""
                }`}
                onClick={() => goToSlide(i)}
              />
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

// ✅ Card Component
const MemberCard = ({ member }) => (
  <div className="ldr-card">
    <div className="ldr-card__img-wrap">
      <img
        src={member.image}
        alt={member.name}
        className="ldr-card__img"
        onError={(e) => {
          e.target.src =
            "https://images.unsplash.com/photo-1544723795-3fb6469f5b39?w=400&q=80";
        }}
      />
    </div>

    <div className="ldr-card__body">
      <h3 className="ldr-card__name">{member.name}</h3>
      <p className="ldr-card__role">{member.designation}</p>
      {member.description && (
        <p className="ldr-card__desc">{member.description}</p>
      )}
    </div>
  </div>
);

export default Leadership;